import { useMemo, useEffect, useState } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import { fetchData } from '../../../../helpers/fetchData'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import {
  addSalesDateWiseReportJob,
  updateSalesDateWiseReportJob,
  removeSalesDateWiseReportJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getOneMonthMaxDate, getMinDate } from '../../../../utils/dateRangeValidation'

const SalesDateWiseReportAsync = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const jobs = useSelector(state => state.app.salesDateWiseReportJobs || [])

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'sales_date_wise_report_async',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      department_id: '',
      format: 'excel'
    }
  )

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addSalesDateWiseReportJob,
    updateJobAction: updateSalesDateWiseReportJob,
    removeJobAction: removeSalesDateWiseReportJob,
    createEndpoint: '/v3/reports/sales-date-wise/jobs',
    statusEndpoint: '/v3/reports/sales-date-wise/jobs',
    downloadEndpoint: '/v3/reports/sales-date-wise/jobs',
    fileNamePrefix: 'sales_date_wise_report',
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      department_id: filters.department_id || undefined,
      format
    })
  })
  const [departments, setDepartments] = useState([])

  useEffect(() => {
    fetchData('POST', '/v3/department/getList', { page: 1, per_page: 1000 })
      .then(response => setDepartments(response?.data || []))
      .catch(error => console.error('Error loading departments:', error))
  }, [])

  const handleGenerate = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleReset = () => {
    resetFilters()
  }

  const handleChange = e => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown',
      name: 'department_id',
      label: 'Department',
      options: departments.map(department => ({ label: department.name_en || department.name || department.name_ar, value: department.id })),
      placeholder: 'All Departments',
      filter: true,
      width: 'w-[180px]'
    },
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('Sales Date Wise Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Sales Date Wise Report')}
      />
    </div>
  )
}

export default SalesDateWiseReportAsync
